'use client';

import { googlePlayUrl } from '@repo/config';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { BsGooglePlay, BsX } from 'react-icons/bs';
import { shouldHideOnPrivatePath } from '@/lib/path';
import { deeplinkFromPathname } from './deeplink-from-pathname';
import { useSmartBannerDismiss } from './use-smart-banner-dismiss';

type DeviceType = 'ios' | 'android' | 'unknown';

const FALLBACK_DELAY_MS = 1500;

function detectDevice(): DeviceType {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  }
  if (/android/.test(ua)) {
    return 'android';
  }
  return 'unknown';
}

export function SmartBanner(): React.JSX.Element | null {
  const pathname = usePathname();
  const { dismissed, dismiss } = useSmartBannerDismiss();
  const [device, setDevice] = useState<DeviceType>('unknown');
  const [mounted, setMounted] = useState(false);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDevice(detectDevice());
    setMounted(true);
    return () => {
      if (fallbackTimer.current !== null) {
        clearTimeout(fallbackTimer.current);
      }
    };
  }, []);

  if (!mounted || dismissed) {
    return null;
  }
  if (device !== 'android') {
    return null;
  }
  if (shouldHideOnPrivatePath(pathname)) {
    return null;
  }

  const deeplink = deeplinkFromPathname(pathname);

  const handleOpen = (): void => {
    if (fallbackTimer.current !== null) {
      clearTimeout(fallbackTimer.current);
    }
    fallbackTimer.current = setTimeout(() => {
      window.location.href = googlePlayUrl;
    }, FALLBACK_DELAY_MS);
    const onVisibilityChange = (): void => {
      if (document.hidden && fallbackTimer.current !== null) {
        clearTimeout(fallbackTimer.current);
        fallbackTimer.current = null;
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-50 flex items-center gap-3 border-b border-border bg-background/95 px-3 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:hidden"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="rounded-full p-1 text-muted-foreground hover:bg-muted"
      >
        <BsX className="size-5" aria-hidden="true" />
      </button>
      <BsGooglePlay className="size-6 text-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">Cool Mate</p>
        <p className="truncate text-xs text-muted-foreground">
          Open in the app for a better experience
        </p>
      </div>
      <a
        href={deeplink}
        onClick={handleOpen}
        className="shrink-0 rounded-lg bg-foreground px-3 py-1.5 text-sm font-semibold text-background"
      >
        Open
      </a>
    </div>
  );
}
